// ============================================================
// Synthese — le prix, ligne par ligne, avec son CALCUL.
// ------------------------------------------------------------
// « Matière 0,98 € » se croit ou ne se croit pas. « Matière — 0,163 kg
// × 6,00 €/kg — 0,98 € » se vérifie de tête en trois secondes. C'est
// toute la différence entre un chiffre qu'on subit et un chiffre qu'on
// contrôle, et c'est ce qui rend la promesse « pas de boîte noire »
// vérifiable au lieu d'être affirmée.
//
// ── Pourquoi la DIVISION par la quantité est écrite ──
//
// Le MONTAGE se compte par lot et la PROGRAMMATION par projet :
// « 65 min ÷ 1 pc × 80 €/h ». À l'unité ils pèsent autant que des
// heures de cycle ; à cinquante pièces ils ont presque disparu. C'est la première chose
// que Charles regardera, et c'est exactement ce que les paliers de
// quantité servent à rendre visible avant de s'engager.
//
// ── Ce qui ne s'affiche pas ──
//
// Une ligne non calculable affiche « — », jamais 0,00 €. Un zéro
// affirme « c'est gratuit » ; un tiret dit « je ne sais pas ». Et si un
// élément indispensable manque, il n'y a AUCUNE ligne de total : les
// lignes calculables restent visibles, le coût partiel s'affiche, mais
// on n'appelle jamais « prix » ce qui n'en est pas un.

import { formatEuroOuTiret, formatMesure, formatNumber } from '../../lib/format.js';
import { ROUNDING_LABELS } from '@shared/pricing.js';
import { LIBELLE_CATEGORIE } from '@shared/gamme.js';

/** Une ligne : son libellé, son opération en clair, son montant. */
function Ligne({ label, calcul, montant, fort = false }) {
  return (
    <div className={fort ? 'synthese__ligne synthese__ligne--fort' : 'synthese__ligne'}>
      <span className="synthese__label">{label}</span>
      <span className="synthese__calcul">{calcul ?? ''}</span>
      <span className="synthese__montant num">{montant}</span>
    </div>
  );
}

const nb = (v, u) => formatMesure(v, u);

export function Synthese({ piece, result }) {
  const d = result?.detail;
  if (!d) return null;

  const qty = Number(piece.qty) || 1;
  const w = d.work ?? {};
  const m = d.material ?? {};
  const complet = result.complete !== false;

  // Les trois temps, séparés : le cycle se MULTIPLIE par la quantité,
  // le montage et la programmation se DIVISENT. C'est la seule chose
  // qu'il faut comprendre pour lire un prix de série.
  // ── Les temps EFFECTIVEMENT calculés, pas ceux validés à la main ──
  //
  // Ces trois lignes lisaient `piece.cycle_time_min` — le champ que le
  // deviseur remplit. Or depuis le 5 août le temps vient de la GAMME, et
  // depuis le 10 août il peut venir de la proposition de l'agent. Tant
  // que rien n'était validé à la main, les trois lignes affichaient donc
  // « — » sous un « Total usinage 229,50 € » parfaitement rempli.
  //
  // Une synthèse dont les lignes ne s'additionnent pas au total est
  // pire qu'une synthèse absente : elle donne à croire que le total sort
  // d'ailleurs. `work` porte les temps que le calcul a réellement
  // employés, quelle qu'en soit la provenance — c'est lui qui fait foi.
  const cycle = Number(w.cycle_time_min) || null;
  const montage = Number(w.setup_time_min) || null;
  const prog = Number(w.programming_time_min) || null;
  const tm = Number(w.machine_rate) || null;
  const th = Number(w.human_rate) || null;

  // Le DÉBIT d'une barre entière est du cycle `par_lot` : la scie tourne
  // une fois pour la série. Il entre dans `cycleCost` côté calcul, et il
  // manquait ici — donc la ligne « Cycle » était plus basse que le total
  // qu'elle est censée composer, de 5 à 10 minutes de sciage. Une
  // synthèse dont les lignes ne s'additionnent pas au total ne se
  // vérifie plus de tête, et c'est toute sa raison d'être.
  const cycleLot = Number(w.cycle_par_lot_min) || 0;
  const coutCycle = (cycle || cycleLot) && tm ? ((cycle * qty + cycleLot) * tm) / 60 : null;
  const coutMontage = montage && th ? (montage * th) / 60 : null;
  const coutProg = prog && th ? (prog * th) / 60 : null;

  // ── LES TROIS TOTAUX DU 11 AOÛT ──
  //
  // `totaux_gamme` vient du calcul, jamais d'un recompte ici : deux
  // additions séparées finissent toujours par diverger, et c'est la
  // synthèse qui aurait tort. Ils valent tous zéro sur un devis déjà
  // chiffré — c'est comme ça qu'on distingue les deux modèles, sans
  // drapeau.
  // `w`, et non `d` : les totaux vivent DANS `work`, avec les temps
  // qu'ils décomposent. Les lire un cran trop haut rendait `{}`, donc
  // `nouveauModele = false`, donc la synthèse affichait « Cycle /
  // Montage / Programmation » sur une gamme qui n'en contient aucun —
  // et le contrôle n'apparaissait nulle part alors qu'il était dans le
  // prix. Les lignes ne s'additionnaient plus au total.
  const g = w.totaux_gamme ?? {};
  const nouveauModele = ['prototype', 'production_piece', 'production_lot', 'controle_lot', 'controle_piece']
    .some((k) => Number(g[k]) > 0);
  // Les totaux de gamme restent DÉTAILLÉS (le contrôle y a ses deux
  // lignes) : c'est ce qui permet d'écrire « 80 de mise au point + 20 de
  // contrôle ». Mais les MONTANTS viennent des temps déjà fondus par le
  // calcul — jamais d'une addition refaite ici.
  // Sur combien de pièces la production est comptée. Vient du CALCUL,
  // jamais recalculé ici : deux règles séparées divergeraient.
  const multProduction = Number(w.production_multiplicateur ?? qty);
  const controlePiece = Number(g.controle_piece) || 0;
  const controleLot = Number(g.controle_lot) || 0;
  // Le contrôle est fondu dans ses deux voisins, mais chacun garde SON
  // taux : le contrôle est humain, l'usinage machine. Sommer les minutes
  // avant le taux mentirait le jour où les deux taux divergent.
  const coutPrototype = montage && th ? (montage * th) / 60 : null;
  const coutProduction = (cycle || cycleLot) && tm
    ? ((cycle * multProduction + cycleLot) * tm) / 60 : null;

  return (
    <div className="synthese">
      <p className="caps">Synthèse{piece.ref ? ` — ${piece.ref}` : ''}</p>

      {/* ── LE TAUX NE S'AFFICHE JAMAIS EN EN-TÊTE ──
          Il y a DEUX taux : machine pour le cycle, humain pour le
          montage et la programmation. Un en-tête « USINAGE — 80 €/h »
          est vrai aujourd'hui parce que les deux coïncident, et devient
          faux SANS AUCUN SIGNE le jour où ils divergent.
          On n'écrit jamais un seul nombre qui représente deux
          variables. */}
      <p className="caps">Usinage — taux de vente : marge et temps morts inclus</p>

      {/* TROIS lignes, pas quatre. Le débit n'a pas de ligne à lui :
          c'est une opération de la gamme, de catégorie `cycle`, et il
          entre dans cette ligne-ci. Le détail se lit dans la gamme, pas
          dans le bloc prix. */}
      {nouveauModele ? (
        <>
          {/* ── LE CONTRÔLE N'A PAS DE LIGNE À LUI — 11 août 2026 ──
              Il en avait une, et Charles a tranché : le contrôle du
              premier article appartient au PROTOTYPE, celui de la série
              à la PRODUCTION. C'est vrai du travail — on ne mobilise pas
              un poste de contrôle, on contrôle ce qu'on vient de faire.

              Le montant, lui, reste calculé sur DEUX taux : le contrôle
              est humain, l'usinage machine. Les deux valent 80 €/h
              aujourd'hui, mais la ligne de calcul écrit les deux parts
              pour que la fusion reste vérifiable. */}
          <Ligne
            label={LIBELLE_CATEGORIE.prototype}
            calcul={
              coutPrototype
                ? `${nb(g.prototype, 'min')} de montage et programmation`
                  + (controleLot ? ` + ${nb(controleLot, 'min')} de contrôle` : '')
                  + ` (par lot) × ${formatEuroOuTiret(th)}/h`
                : null
            }
            montant={formatEuroOuTiret(coutPrototype)}
          />
          {/* ── LA PREMIÈRE PIÈCE EST DANS LE PROTOTYPE, ET ÇA SE DIT ──
              La production ne porte que sur `qté − 1`. À une pièce elle
              vaut zéro — et un « 0,00 € » sans explication se lirait
              comme un oubli. La ligne de calcul écrit donc sur combien
              de pièces on compte, et pourquoi. */}
          <Ligne
            label="Production"
            calcul={
              multProduction === 0
                ? `${nb(g.production_piece, 'min')} × 0 pièce — la première est dans la mise en route`
                : coutProduction
                  ? `${nb(g.production_piece, 'min')}`
                    + (controlePiece ? ` + ${nb(controlePiece, 'min')} de contrôle` : '')
                    + ` × ${multProduction} pc sur ${qty}`
                    + (g.production_lot ? ` + ${nb(g.production_lot, 'min')} de débit (par lot)` : '')
                  : null
            }
            montant={formatEuroOuTiret(coutProduction)}
          />
        </>
      ) : (
        <>
          <Ligne
            label="Cycle"
            calcul={
              (cycle || cycleLot) && tm
                ? `${nb(cycle, 'min')} × ${qty} pc`
                  + (cycleLot ? ` + ${nb(cycleLot, 'min')} de débit (par lot)` : '')
                  + ` = ${nb(cycle * qty + cycleLot, 'min')} × ${formatEuroOuTiret(tm)}/h machine`
                : null
            }
            montant={formatEuroOuTiret(coutCycle)}
          />

          {/* La DIVISION est écrite. C'est là que se voit l'amortissement. */}
          <Ligne
            label="Montage"
            calcul={
              montage && th
                ? `${nb(montage, 'min')} (par lot) × ${formatEuroOuTiret(th)}/h humain`
                : null
            }
            montant={formatEuroOuTiret(coutMontage)}
          />
          <Ligne
            label="Programmation"
            calcul={
              prog && th
                ? `${nb(prog, 'min')} (par projet) × ${formatEuroOuTiret(th)}/h humain`
                : null
            }
            montant={formatEuroOuTiret(coutProg)}
          />
        </>
      )}

      {/* Le coefficient de SÉCURITÉ ne se glisse pas : il porte sa
          ligne, son facteur et le montant qu'il ajoute. C'est ce qui le
          distingue de la marge globale supprimée le 5 août — celle-là
          était invisible dans un réglage. */}
      {d.coefficient_securite_montant != null ? (
        <Ligne
          label="Coefficient de sécurité"
          calcul={`${formatEuroOuTiret(d.usinage_avant_coefficient)} × ${formatNumber(d.coefficient_securite)}`}
          montant={`+ ${formatEuroOuTiret(d.coefficient_securite_montant)}`}
        />
      ) : null}

      <div className="synthese__trait" />
      <Ligne label="Total usinage" montant={formatEuroOuTiret(d.usinage_lot)} fort />

      {complet ? (
        <>
          <Ligne
            label="Minimum de facturation"
            montant={d.min_invoice_applied ? formatEuroOuTiret(d.min_invoice) : '—'}
          />
          <Ligne
            label="Arrondi"
            calcul={
              d.rounding_rule && d.rounding_rule !== 'none'
                ? (ROUNDING_LABELS[d.rounding_rule] ?? d.rounding_rule)
                : null
            }
            montant={
              d.rounding_amount ? `+ ${formatEuroOuTiret(d.rounding_amount)}` : '—'
            }
          />

          <Ligne
            label={`Prix unitaire usinage (${qty} pièce${qty > 1 ? 's' : ''})`}
            montant={formatEuroOuTiret(d.price_unit)}
            fort
          />

          {/* ── Les trois lignes HORS ENGAGEMENT ──
              Mertens n'est responsable ni du prix ni de la disponibilité
              de la matière : ça bouge sur le marché, et sur une commande
              le prix peut changer six mois plus tard. Elles sortent
              séparées, jamais fondues dans le prix pièce. */}
          <div className="synthese__trait" />
          <p className="caps">Matière et transport — hors engagement, indicatif</p>
          <Ligne
            label="Matière"
            calcul={
              m.purchased_mass_kg && m.price_per_kg
                ? `${nb(m.purchased_mass_kg, 'kg')} × ${formatEuroOuTiret(m.price_per_kg)}/kg × ${formatNumber(d.coefficient_matiere ?? 1)}`
                : (m.masse_inconnue_raison ?? null)
            }
            montant={formatEuroOuTiret(d.ligne_matiere || null)}
          />
          {/* ── CE QUI N'EST PAS DANS LE MONTANT SE DIT ──
              Un traitement sans prix ne bloque plus le devis (décision
              du 11 août 2026), mais il ne vaut pas zéro pour autant : il
              est écarté du total, et cette ligne le NOMME. Sans elle, le
              montant serait juste et le devis faux — un traitement
              offert au client, invisible. */}
          <Ligne
            label="Traitement / sous-traitance"
            calcul={
              d.traitements_sans_prix
                ? `+ ${d.traitements_sans_prix} à confirmer, non compté${d.traitements_sans_prix > 1 ? 's' : ''}`
                : null
            }
            montant={formatEuroOuTiret(d.ligne_traitement || null)}
          />
          <Ligne label="Transport matière" montant={formatEuroOuTiret(d.ligne_transport || null)} />

          {/* ── Un total qui masque une ligne inconnue est un FAUX total ──
              `total_hors_engagement` vaut `null` quand le prix matière
              est inconnu. Le `|| 0` qui traînait ici l'additionnait à
              zéro : le TOTAL HT affichait le seul usinage, en gras, avec
              toutes les apparences d'un total complet — et la matière
              lue comme gratuite.
              C'est le cinquième motif de coercition du tour de l'invariant
              12 : une fonction de conversion est un lieu de naissance, et
              `|| 0` transforme une absence en zéro hors de toute
              validation. */}
          <div className="synthese__trait" />
          <Ligne
            label="TOTAL HT"
            montant={
              d.total_hors_engagement == null
                ? '—'
                : formatEuroOuTiret(Number(d.price_total) + Number(d.total_hors_engagement))
            }
            fort
          />
          {d.total_hors_engagement == null ? (
            <p className="field__hint">
              Le prix matière n’est pas connu : il n’y a pas de total. Les lignes ci-dessus sont
              justes, et le prix d’usinage — <strong>{formatEuroOuTiret(d.price_total)}</strong> —
              est celui que Mertens engage. Saisissez un montant matière, ou déposez une offre
              fournisseur.
            </p>
          ) : null}
          <p className="field__hint">
            Les prix matière, traitement et transport sont donnés à titre indicatif au jour du
            devis et seront reconfirmés à la commande.
          </p>
        </>
      ) : (
        // « Prix indisponible » annonçait un blocage qui n'existe plus.
        // Ce qui reste, c'est un GESTE qui n'a pas encore eu lieu — et
        // le message le nomme au lieu de constater une panne.
        <p className="field__hint">
          <strong>Pas encore de prix</strong> — {d.prix_indisponible} Les lignes ci-dessus sont
          calculées ; ce qui relève de la vente — minimum de facturation, arrondi — ne s’applique
          qu’à un usinage chiffré.
        </p>
      )}
    </div>
  );
}

export default Synthese;
